import { SupplyGate, SupplyLegacyRedirect } from './pages/supplyNavigation';
import HomePage from './pages/HomePage';
import PageBoundary from './components/PageBoundary';
import { hasProcurementQuery } from './homeNavigation';
import React, { lazy, useState } from "react";
import { Layout, Menu, ConfigProvider, theme, Button, Drawer } from "antd";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import {
  DashboardOutlined, ShoppingOutlined, UserOutlined, LineChartOutlined,
  MessageOutlined, GlobalOutlined, DollarOutlined, AuditOutlined,
  RocketOutlined, BarChartOutlined, ShopOutlined, TeamOutlined, SafetyOutlined,
  NodeIndexOutlined, FundOutlined, ExperimentOutlined, LogoutOutlined, PartitionOutlined,
  PictureOutlined, MenuOutlined, SettingOutlined, CustomerServiceOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, FileSearchOutlined, SafetyCertificateOutlined,
  CloudUploadOutlined, DeploymentUnitOutlined
} from "@ant-design/icons";
import { filterMenu, isOnlyPublicAccess } from "./menuAccess";
import { legalAPI } from './api';
import { canManage } from "./pages/personalWorkspaceUi";
import type { PersonalAccess } from "./personalWorkspaceTypes";
import Logo from "./components/Logo";

import { getToken, setToken, clearToken, tokenAlive, adminAPI, caAPI, personalWorkspaceAPI } from "./api";
import "./App.css";

// 苹果风：浅灰侧边栏 + 黑字(macOS 设置风)，SF/苹方字体
const SupplyDirectorPage = lazy(() => import('./pages/SupplyDirectorPage'));
const DullStockPage = lazy(() => import('./pages/DullStockPage'));
const ProductionPage = lazy(() => import('./pages/ProductionPage'));
const CreativePage = lazy(() => import('./pages/CreativePage'));
const GeoPage = lazy(() => import('./pages/GeoPage'));
const EcommercePage = lazy(() => import('./pages/EcommercePage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const KnowledgeContributePage = lazy(() => import('./pages/KnowledgeContributePage'));
const CrossBorderPage = lazy(() => import('./pages/CrossBorderPage'));
const WaimaoPricePage = lazy(() => import('./pages/WaimaoPricePage'));
const FtcrmWorkbenchPage = lazy(() => import('./pages/FtcrmWorkbenchPage'));
const FtcrmBiPage = lazy(() => import('./pages/FtcrmBiPage'));
const FtcrmCustomerPage = lazy(() => import('./pages/FtcrmCustomerPage'));
const FtcrmPipelinePage = lazy(() => import('./pages/FtcrmPipelinePage'));
const FtcrmMailPage = lazy(() => import('./pages/FtcrmMailPage'));
const FtcrmWecomPage = lazy(() => import('./pages/FtcrmWecomPage'));
const FtcrmWhatsAppPage = lazy(() => import('./pages/FtcrmWhatsAppPage'));
const FtcrmMailAccountsPage = lazy(() => import('./pages/FtcrmMailAccountsPage'));
const CommissionPage = lazy(() => import('./pages/CommissionPage'));
const ReconciliationPage = lazy(() => import('./pages/ReconciliationPage'));
const FollowupWorkspace = lazy(() => import('./pages/FollowupWorkspace'));
const DeliveryDashboardPage = lazy(() => import('./pages/DeliveryDashboardPage'));
const ExecutivePage = lazy(() => import('./pages/ExecutivePage'));
const CockpitPage = lazy(() => import('./pages/CockpitPage'));
const InvestmentPage = lazy(() => import('./pages/InvestmentPage'));
const HomedecorPage = lazy(() => import('./pages/HomedecorPage'));
const HomedecorStorePage = lazy(() => import('./pages/HomedecorStorePage'));
const HomedecorFinancePage = lazy(() => import('./pages/HomedecorFinancePage'));
const FormulaConfigPage = lazy(() => import('./pages/FormulaConfigPage'));
const PeBomAuditPage = lazy(() => import('./pages/PeBomAuditPage'));
const PeProjectPage = lazy(() => import('./pages/PeProjectPage'));
const RndProcurementPage = lazy(() => import('./pages/RndProcurementPage'));
const EngDrawingPage = lazy(() => import('./pages/EngDrawingPage'));
const QualityPage = lazy(() => import('./pages/QualityPage'));
const ProductConfigPage = lazy(() => import('./pages/ProductConfigPage'));
const KnowledgeSuggestionPage = lazy(() => import('./pages/KnowledgeSuggestionPage'));
const ItKnowledgePage = lazy(() => import('./pages/ItKnowledgePage'));
const EngDocsPage = lazy(() => import('./pages/EngDocsPage'));
const KbHomePage = lazy(() => import('./pages/KbHomePage'));
const BizKnowledgePage = lazy(() => import('./pages/BizKnowledgePage'));
const ChiefArchitectPage = lazy(() => import('./pages/ChiefArchitectPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const FtcrmAskPage = lazy(() => import('./pages/FtcrmAskPage'));
const PersonalWorkspacePage = lazy(() => import('./pages/PersonalWorkspacePage'));
const LegalWorkbenchPage = lazy(() => import('./pages/LegalWorkbenchPage'));
const PersonalWorkspaceAdminPage = lazy(() => import('./pages/PersonalWorkspaceAdminPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const CustomerServicePage = lazy(() => import('./pages/CustomerServicePage'));
const ShipmentPage = lazy(() => import('./pages/ShipmentPage'));
const AccountBalancePage = lazy(() => import('./pages/AccountBalancePage'));
const SubsidiaryLedgerPage = lazy(() => import('./pages/SubsidiaryLedgerPage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));
const ChannelProfitPage = lazy(() => import('./pages/ChannelProfitPage'));
const FinanceCenterPage = lazy(() => import('./pages/FinanceCenterPage'));
const EngineeringDocPage = lazy(() => import('./pages/EngineeringDocPage'));
const MaterialAckPage = lazy(() => import('./pages/MaterialAckPage'));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage'));
const WuyouPage = lazy(() => import('./pages/WuyouPage'));
const JdUePendingPage = lazy(() => import('./pages/JdUePendingPage'));
const BizServiceAgentPage = lazy(() => import('./pages/BizServiceAgentPage'));
const WorkorderFilesPage = lazy(() => import('./pages/WorkorderFilesPage'));
const MyTodosPage = lazy(() => import('./pages/MyTodosPage'));
const ServiceOrderPage = lazy(() => import('./pages/ServiceOrderPage'));
const ServiceFeeConfirmPage = lazy(() => import('./pages/ServiceFeeConfirmPage'));
const ServiceSettlementPage = lazy(() => import('./pages/ServiceSettlementPage'));
const SalesJourneyPage = lazy(() => import('./pages/SalesJourneyPage'));
const ServiceAgentAreaPage = lazy(() => import('./pages/ServiceAgentAreaPage'));
const SalesOrderPage = lazy(() => import('./pages/SalesOrderPage'));
const CustomOrderPage = lazy(() => import('./pages/CustomOrderPage'));
const CustomConfigPage = lazy(() => import('./pages/CustomConfigPage'));
const WuyouTasksPage = lazy(() => import('./pages/WuyouTasksPage'));
const Customer360Page = lazy(() => import('./pages/Customer360Page'));
const UdcPage = lazy(() => import('./pages/UdcPage'));
const RebatePage = lazy(() => import('./pages/RebatePage'));
const WuyouFlowPage = lazy(() => import('./pages/WuyouFlowPage'));
const SfLoginPage = lazy(() => import('./pages/sf/SfLoginPage'));
const ServiceWorkspace = lazy(() => import('./pages/sf/ServiceWorkspace'));
const SfOrdersPage = lazy(() => import('./pages/sf/SfOrdersPage'));
const SfOrderDetailPage = lazy(() => import('./pages/sf/SfOrderDetailPage'));
const SfFeesPage = lazy(() => import('./pages/sf/SfFeesPage'));
const HdmHome = lazy(() => import('./pages/hdm/HdmHome'));
const HdmCreate = lazy(() => import('./pages/hdm/HdmCreate'));
const HdmCustomers = lazy(() => import('./pages/hdm/HdmCustomers'));
const HdmFollowup = lazy(() => import('./pages/hdm/HdmFollowup'));
const HdmTeam = lazy(() => import('./pages/hdm/HdmTeam'));
const HdmRisk = lazy(() => import('./pages/hdm/HdmRisk'));
const SupplyV2Workspace = lazy(() => import('./pages/SupplyV2Workspace'));
const ModuleChatBubble = lazy(() => import('./components/ModuleChatBubble'));

const APPLE_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro SC", "SF Pro Text", "PingFang SC", "Microsoft YaHei", "Segoe UI", system-ui, sans-serif';
const SIDE_BG = "#f5f5f7";       // 浅灰侧栏(与气泡/对话页同一设计语言)
const SIDE_BORDER = "1px solid rgba(0,0,0,0.06)";

const { Sider, Content } = Layout;

const menuItems = [
  { key: "/home", alwaysVisible: true, icon: <DashboardOutlined />, label: <Link to="/home">工作首页</Link> },
  {
    key: "supply-chain",
    icon: <NodeIndexOutlined />,
    label: "供应链模块",
    children: [
      { key: "/", permKeys: ["/", "/purchase-forecast", "/bom-engineering"], icon: <ShoppingOutlined />, label: <Link to="/?workspace=procurement">采购建议</Link> },
      { key: "/production", icon: <ShoppingOutlined />, label: <Link to="/production">生产建议</Link> },
      { key: "/supply-rnd-procurement", permKey: "/", icon: <ExperimentOutlined />, label: <Link to="/supply-rnd-procurement">研发采购工作台</Link> },
      { key: "/followup", icon: <RocketOutlined />, label: <Link to="/followup">采购跟单</Link> },
      { key: "/supplier-profile", permKeys: ["/supplier-profile", "/price-list"], icon: <TeamOutlined />, label: <Link to="/supplier-profile">供应商画像</Link> },
      { key: "/dull-stock", icon: <ShoppingOutlined />, label: <Link to="/dull-stock">呆滞库存治理</Link> },
      { key: "/delivery", icon: <NodeIndexOutlined />, label: <Link to="/delivery">交付看板</Link> },
      { key: "/production-director", permKey: "/production", icon: <DashboardOutlined />, label: <Link to="/production-director">生产总监驾驶舱</Link> },
    ],
  },
  {
    key: "ecommerce",
    icon: <ShopOutlined />,
    label: "电商模块",
    children: [
      { key: "/ecommerce", icon: <ShopOutlined />, label: <Link to="/ecommerce">工作台</Link> },
      { key: "/influencer", icon: <UserOutlined />, label: <Link to="/influencer">达人合作</Link> },
      // 2026-08-31 撤掉「佣金追踪」菜单：那一页是 8.x 脚手架残留的假数据（详见 CommissionPage.tsx
      // 文件头）。/commission 路由保留成「未上线」占位，老书签点进来不至于 404。
      // 渠道分账按《10.5兼容性评估》N7 重建后再挂回菜单。
      { key: "/competitor", icon: <BarChartOutlined />, label: <Link to="/competitor">竞品</Link> },
      { key: "/wanxiang", icon: <FundOutlined />, label: <Link to="/wanxiang">推广</Link> },
      { key: "/jd-sales", icon: <LineChartOutlined />, label: <Link to="/jd-sales">销售</Link> },
      { key: "/ops-persist", icon: <AuditOutlined />, label: <Link to="/ops-persist">待办</Link> },
    ],
  },
  {
    key: "creative-marketing",
    icon: <PictureOutlined />,
    label: "创意营销",
    children: [
      { key: "/creative", icon: <PictureOutlined />, label: <Link to="/creative">创意工厂</Link> },
      { key: "/geo", icon: <GlobalOutlined />, label: <Link to="/geo">GEO内容</Link> },
    ],
  },
  {
    key: "finance",
    icon: <FundOutlined />,
    label: "财务模块",
    children: [
      { key: "/reconciliation", icon: <AuditOutlined />, label: <Link to="/reconciliation">供应商对账</Link> },
      { key: "/finance-center", icon: <FundOutlined />, label: <Link to="/finance-center">财务中心</Link> },
    ],
  },
  {
    key: "engineering",
    icon: <AuditOutlined />,
    label: "工程资料中心",
    children: [
      // 物料承认书 2026-08-24 挪进「知识库」组——它是知识资产（供应商技术底档、
      // 喂 IQC 检验标准），跟这里的"请款/结算资料生成"是两件事，混在一起找不着。
      { key: "/engineering-docs", icon: <AuditOutlined />, label: <Link to="/engineering-docs">资料生成</Link> },
    ],
  },
  {
    // 产品工程中心（10.6）：目前只有 BOM 审计一页，后续按 10.6 §6 的 16 个栏目扩。
    // 权限键在 admin_store.MODULES 的 product-engineering 组，两边必须同时登记
    // （只加菜单不登记权限目录 = 功能上线了但非管理员看不见，而开发自己是管理员测不出来）
    key: "product-engineering",
    icon: <PartitionOutlined />,
    label: "产品工程中心",
    children: [
      { key: "/pe-project", icon: <PartitionOutlined />, label: <Link to="/pe-project">产品开发项目</Link> },
      { key: "/pe-rnd-procurement", permKey: "/pe-project", icon: <ExperimentOutlined />, label: <Link to="/pe-rnd-procurement">研发采购协同</Link> },
      { key: "/pe-bom-audit", icon: <PartitionOutlined />, label: <Link to="/pe-bom-audit">BOM 审计</Link> },
      { key: "/eng-drawing", icon: <PartitionOutlined />, label: <Link to="/eng-drawing">工程图纸台账</Link> },
    ],
  },
  {
    key: "quality",
    icon: <ExperimentOutlined />,
    label: "质量模块",
    children: [
      { key: "/quality/iqc", icon: <ExperimentOutlined />, label: <Link to="/quality/iqc">IQC 来料检验</Link> },
      { key: "/quality/oqc", icon: <ExperimentOutlined />, label: <Link to="/quality/oqc">OQC 出货检验</Link> },
      { key: "/product-config", icon: <PartitionOutlined />, label: <Link to="/product-config">产品配置防错</Link> },
    ],
  },
  {
    // 外贸 AI CRM（10.7）：在原「外贸模块」上增量重构，**父键 foreign-trade 不动**
    // —— 现网 35 人已授过它，改键会让他们突然打不开页面。
    // 只挂有后端支撑的页；邮件/EDM/搜客/海关等做出来再挂，不先摆空菜单。
    key: "foreign-trade",
    icon: <GlobalOutlined />,
    label: "外贸 AI CRM",
    children: [
      { key: "/ftcrm/workbench", icon: <DashboardOutlined />, label: <Link to="/ftcrm/workbench">我的工作台</Link> },
      { key: "/ftcrm/bi", icon: <BarChartOutlined />, label: <Link to="/ftcrm/bi">外贸 BI 报表</Link> },
      { key: "/ftcrm/customers", icon: <TeamOutlined />, label: <Link to="/ftcrm/customers">客户中心</Link> },
      { key: "/ftcrm/pipeline", icon: <FundOutlined />, label: <Link to="/ftcrm/pipeline">询盘与商机</Link> },
      { key: "/ftcrm/mail", icon: <MessageOutlined />, label: <Link to="/ftcrm/mail">邮件中心</Link> },
      { key: "/ftcrm/mail-accounts", icon: <SettingOutlined />, label: <Link to="/ftcrm/mail-accounts">邮箱账号</Link> },
      { key: "/ftcrm/wecom", icon: <MessageOutlined />, label: <Link to="/ftcrm/wecom">企微聊天</Link> },
      { key: "/ftcrm/whatsapp", icon: <MessageOutlined />, label: <Link to="/ftcrm/whatsapp">WhatsApp</Link> },
      { key: "/waimao-price", icon: <DollarOutlined />, label: <Link to="/waimao-price">外贸产品功能价格表</Link> },
      { key: "/cross-border", icon: <GlobalOutlined />, label: <Link to="/cross-border">跨境独立站</Link> },
    ],
  },
  {
    key: "knowledge",
    icon: <FundOutlined />,
    label: "知识库",
    // 2026-08-24 入口统一：公司所有知识都从这一组进——原先承认书在「工程资料中心」、
    // 投稿是顶级独立项，找东西得先记住它在哪个菜单，那本身就是知识管理的失败。
    // 数据层**不合并**（承认书有 OCR 状态机+料号路径、投稿有审核流、工程文档按平台
    // 且受"仅推理导出"约束），统一的是入口、导航和「一次搜全」。
    children: [
      { key: "/kb-home", icon: <FundOutlined />, label: <Link to="/kb-home">知识总览</Link> },
      // 投稿对所有登录用户开放（2026-08-10 用户拍板）：944 个账号里 765 个没有任何
      // 模块权限，他们手上恰恰有最多没沉淀的资料。挪进本组后靠 alwaysVisible 保住可见性。
      { key: "/knowledge-contribute", alwaysVisible: true, icon: <CloudUploadOutlined />,
        label: <Link to="/knowledge-contribute">知识投稿</Link> },
      { key: "/material-ack", icon: <FileSearchOutlined />, label: <Link to="/material-ack">物料承认书</Link> },
      // 业务知识（国标/说明书/GEO/电商详情页/客服话术）：早就入库、AI 搜得到，
      // 但 2026-08-24 前没有入口——用户放了东西却看不见
      { key: "/biz-knowledge", icon: <FundOutlined />, label: <Link to="/biz-knowledge">业务知识</Link> },
      { key: "/it-knowledge", icon: <FileSearchOutlined />, label: <Link to="/it-knowledge">IT运维知识库</Link> },
      { key: "/eng-docs", icon: <ExperimentOutlined />, label: <Link to="/eng-docs">工程文档台账</Link> },
      { key: "/knowledge-suggestion", icon: <ExperimentOutlined />, label: <Link to="/knowledge-suggestion">反馈与采纳</Link> },
    ],
  },
  { type: "divider" as const },
  {
    key: "investment", icon: <TeamOutlined />, label: "招商管理",
    children: [
      { key: "/investment", icon: <ShopOutlined />, label: <Link to="/investment">商家管理</Link> },
      { key: "/investment/homedecor", icon: <TeamOutlined />, label: <Link to="/investment/homedecor">家装潜在客户</Link> },
      { key: "/homedecor-store", icon: <ShopOutlined />, label: <Link to="/homedecor-store">家装门店运营</Link> },
      { key: "/homedecor-finance", icon: <SafetyOutlined />, label: <Link to="/homedecor-finance">家装风控考核</Link> },
    ],
  },
  {
    key: "executive", icon: <LineChartOutlined />, label: "总裁面板",
    children: [
      { key: "/cockpit", icon: <DashboardOutlined />, label: <Link to="/cockpit">战略驾驶舱</Link> },
      { key: "/executive", icon: <LineChartOutlined />, label: <Link to="/executive">经营总览</Link> },
      { key: "/shipment", icon: <ShoppingOutlined />, label: <Link to="/shipment">出货查询</Link> },
    ],
  },
  { key: "/chat", icon: <MessageOutlined />, label: <Link to="/chat">AI 对话</Link> },
  // 知识投稿已于 2026-08-24 挪进「知识库」组（见上），仍保持 alwaysVisible：
  // 后端 /upload 与 /my 本来就只校验登录（/my 只回自己投的稿）。
  { key: "/customer-service", icon: <CustomerServiceOutlined />, label: <Link to="/customer-service">客服工作台</Link> },
  {
    key: "wuyou", icon: <SafetyCertificateOutlined />, label: "无忧服务",
    children: [
      // 顺序按用户指定(复刻 MIS 无忧服务)；完工图片改为嵌在单据详情内不做菜单；焕新折叠靠后
      // 客户全景入口在 客服工作台→客服中心 页签(用户指定)；/wuyou/customer360 路由保留给建单防重提醒的深链
      { key: "/wuyou/sales-orders", icon: <ShoppingOutlined />, label: <Link to="/wuyou/sales-orders">销售订单</Link> },
      { key: "/wuyou/custom-orders", icon: <FileSearchOutlined />, label: <Link to="/wuyou/custom-orders">定制工单</Link> },
      { key: "/wuyou/flow", icon: <RocketOutlined />, label: <Link to="/wuyou/flow">办理工作台</Link> },
      { key: "/wuyou/todo-tasks", icon: <RocketOutlined />, label: <Link to="/wuyou/todo-tasks">待办任务</Link> },
      { key: "/wuyou/done-tasks", icon: <AuditOutlined />, label: <Link to="/wuyou/done-tasks">已办任务</Link> },
      { key: "/wuyou/orders", icon: <FileSearchOutlined />, label: <Link to="/wuyou/orders">安装/维修派单</Link> },
      // 京东自营店上门安装工单：拉进来先待确认，确认后生成上面的「安装/维修派单」工单
      // 2026-09-16 用户拍板改名「京仓现货」(只改显示名; 键/路由不动, 否则后台已勾的授权全作废)
      { key: "/wuyou/jd-pending", icon: <ShoppingOutlined />, label: <Link to="/wuyou/jd-pending">京仓现货</Link> },
      { key: "/wuyou/fee-confirm", icon: <DollarOutlined />, label: <Link to="/wuyou/fee-confirm">服务费用明细</Link> },
      { key: "/wuyou/settlements", icon: <DollarOutlined />, label: <Link to="/wuyou/settlements">服务结算单</Link> },
      { key: "/wuyou/custom-config", icon: <PartitionOutlined />, label: <Link to="/wuyou/custom-config">定制工单配置</Link> },
      { key: "/wuyou/agents", icon: <TeamOutlined />, label: <Link to="/wuyou/agents">服务商配置</Link> },
      { key: "/wuyou/agent-areas", icon: <GlobalOutlined />, label: <Link to="/wuyou/agent-areas">服务商工单属性</Link> },
      { key: "/wuyou/journeys", icon: <BarChartOutlined />, label: <Link to="/wuyou/journeys">销售流水</Link> },
      { key: "/wuyou/rebate", icon: <RocketOutlined />, label: <Link to="/wuyou/rebate">焕新管理</Link> },
    ],
  },
  { key: "/system-health", icon: <DashboardOutlined />, label: <Link to="/system-health">系统体检</Link> },
  {
    key: "system", icon: <SettingOutlined />, label: "系统配置",
    children: [
      // 通用字典(UDC)：全站下拉的统一取值口，管理员维护
      { key: "/system/udc", icon: <PartitionOutlined />, label: <Link to="/system/udc">通用字典</Link> },
      // 公式配置中心：业务公式权重/阈值热改，业务负责人自己维护
      { key: "/formula-config", icon: <ExperimentOutlined />, label: <Link to="/formula-config">公式配置中心</Link> },
    ],
  },
];

type Access = { is_admin: boolean; modules: string[] | "all"; loaded: boolean };

function AppLayout({ onLogout }: { onLogout: () => void }) {
  const location = useLocation();
  const [mobile, setMobile] = React.useState(typeof window !== "undefined" && window.innerWidth < 768);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [access, setAccess] = React.useState<Access>({ is_admin: false, modules: "all", loaded: false });
  // 总架构师身份**单独问一个接口**，不从 access.modules 推导：该权限独立于 S 级与
  // 后台模块授权之外（2026-09-13 拍板第四条），且 adminAPI.me 取不到它。
  // 失败一律按 false（fail-closed）——与 access 那边"失败默认全可见"刻意相反：
  // 菜单看错了只是难用，这个看错了等于把架构中枢的门开给所有人。
  // caLoaded 是必需的：没有它，总架构师直接敲 /chief-architect 网址进来时，whoami
  // 还没回来 isCA 就是 false，路由守卫会先把他弹回首页（看起来就像"权限没生效"）。
  const [ca, setCa] = React.useState({ is: false, loaded: false });
  const isCA = ca.is;
  const [personalAccess, setPersonalAccess] = React.useState<PersonalAccess>();
  const [legalVisible, setLegalVisible] = React.useState(false);

  React.useEffect(() => {
    let current = true;
    legalAPI.materialAccess().then(r => { if (current) setLegalVisible(r.data.available === true); })
      .catch(() => { if (current) setLegalVisible(false); });
    return () => { current = false; };
  }, [location.pathname]);

  React.useEffect(() => {
    let current = true;
    personalWorkspaceAPI.access().then(r => { if (current) setPersonalAccess(r.data); })
      .catch(() => { if (current) setPersonalAccess(undefined); });
    return () => { current = false; };
  }, [location.pathname]);

  React.useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // 取当前用户的后台可见性（过滤菜单/路由）；失败默认全可见，不锁人
  React.useEffect(() => {
    adminAPI.me()
      .then(r => setAccess({ is_admin: !!r.data?.is_admin, modules: r.data?.modules ?? "all", loaded: true }))
      .catch(() => setAccess({ is_admin: false, modules: "all", loaded: true }));
    caAPI.whoami()
      .then(r => setCa({ is: !!r.data?.is_chief_architect, loaded: true }))
      .catch(() => setCa({ is: false, loaded: true }));
  }, []);
  // 导航后关闭移动端抽屉
  React.useEffect(() => { setDrawerOpen(false); }, [location]);
  // (已移除「每次导航把令牌续在网址上」——令牌留在网址会被转发，导致别人点开就以你的身份登录串号；登录靠 localStorage 维持)

  const [collapsed, setCollapsed] = useState(false);
  const brandEl = (collapsed: boolean) => (
    <div style={{ color: "#1d1d1f", fontSize: 17, fontWeight: 600, padding: collapsed ? "18px 0" : "18px 16px", borderBottom: SIDE_BORDER, display: "flex", alignItems: "center", justifyContent: "center", gap: 9 }}>
      <Logo size={26} />
      {!collapsed && <span style={{ letterSpacing: 0.5 }}>天能AI</span>}
    </div>
  );
  // 按可见模块过滤菜单；管理员追加「后台管理」。
  // 过滤逻辑抽到 menuAccess.ts 并有测试守着——"765 个无权限账号必须看得见知识投稿"
  // 这条容易在改菜单时被悄悄破坏（他们本来就看不到别的东西，坏了也没人立刻发现）。
  const isAll = access.modules === "all";
  const filteredItems = filterMenu(
    menuItems as any[], isAll, isAll ? [] : (access.modules as string[]));
  // 总架构师即使一个业务模块都没授权，也不该被当成"无权限账号"轰去投稿页
  const onlyPublicAccess = isOnlyPublicAccess(filteredItems, isAll, access.loaded) && !isCA;
  // 中央中心为独立主导航；仍仅由 whoami 判定，不进入普通管理员权限目录。
  const caChild = {
    key: "/chief-architect", icon: <DeploymentUnitOutlined />,
    label: <Link to="/chief-architect">总架构师中央控制中心</Link>,
  };
  const withCA = (items: any[]): any[] => {
    if (!isCA) return items;
    return [...items, caChild];
  };
  // as any[]：menuAccess.MenuNode 是刻意宽松的可测类型，AntD 的 ItemType 更严格
  const finalItems = (access.is_admin
    ? [...withCA(filteredItems).map((item: any) => item.key === 'system' ? { ...item, children: [...(item.children || []),
        { key: 'ftcrm-customer-permissions', icon: <SettingOutlined />, label: <Link to="/admin?tab=roles">外贸 CRM 客户权限</Link> }] } : item), { type: "divider" as const },
       { key: "/admin", icon: <SettingOutlined />, label: <Link to="/admin">后台管理</Link> }]
    : withCA(filteredItems))
    // 2026-10-30：总裁面板、系统配置暂不使用，隐藏整组入口；保留路由和原权限，便于恢复。
    .filter((item: any) => item.key !== "executive" && item.key !== "system") as any[];
  // 个人空间独立按当前员工身份及后端能力发现展示，不继承业务模块全量授权。
  // 独立入口排在知识库之后，按各自权限展示。
  const workspaceItems: any[] = [];
  if (legalVisible) workspaceItems.push({ key: '/legal', icon: <SafetyCertificateOutlined />,
    label: <Link to="/legal">法务与合规</Link> });
  if (personalAccess) {
    workspaceItems.push({ key: '/personal-workspace', icon: <UserOutlined />,
      label: <Link to="/personal-workspace">我的空间</Link> });
    if (canManage(personalAccess)) workspaceItems.push({ key: '/personal-workspace/admin', icon: <SafetyCertificateOutlined />,
      label: <Link to="/personal-workspace/admin">空间管理</Link> });
  }
  const knowledgeIndex = finalItems.findIndex(item => item?.key === 'knowledge');
  finalItems.splice(knowledgeIndex < 0 ? finalItems.length : knowledgeIndex + 1, 0, ...workspaceItems);
  // 进页面时菜单分组默认全部合上,只展开当前路由所在的那一组
  const parentOfPath = (path: string): string => {
    for (const it of menuItems as any[]) {
      if (it?.children?.some((c: any) => c.key === path)) return it.key;
    }
    return "";
  };
  // 根地址现在是工作首页，不能再按旧采购路由默认展开供应链。
  const activeMenuPath = location.pathname === "/" && !hasProcurementQuery(location.search) ? "/home" : location.pathname;
  const menuEl = (
    <Menu
      className="side-menu"
      theme="light"
      mode="inline"
      selectedKeys={[activeMenuPath]}
      defaultOpenKeys={parentOfPath(activeMenuPath) ? [parentOfPath(activeMenuPath)] : []}
      items={finalItems}
      style={{ borderRight: 0, background: "transparent" }}
    />
  );
  const logoutEl = (collapsed: boolean) => (
    <div className="logout-item" onClick={onLogout}
      style={{ flexShrink: 0, padding: collapsed ? "14px 0" : "14px 24px", color: "#515154", cursor: "pointer",
        borderTop: SIDE_BORDER, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10, fontSize: 13.5 }}>
      <LogoutOutlined />{!collapsed && " 退出登录"}
    </div>
  );
  const toggleEl = (
    <div className="side-toggle" onClick={() => setCollapsed(c => !c)}
      style={{ flexShrink: 0, padding: collapsed ? "12px 0" : "12px 24px", color: "#515154", cursor: "pointer",
        borderTop: SIDE_BORDER, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10, fontSize: 13.5 }}>
      {collapsed ? <MenuUnfoldOutlined /> : <><MenuFoldOutlined /> 收起侧栏</>}
    </div>
  );
  // 侧栏整体：品牌 + 可滚动菜单 + 收起/退出（desktop 才有收起按钮+折叠态）
  const buildSide = (isDesktop: boolean) => (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: SIDE_BG }}>
      {brandEl(isDesktop && collapsed)}
      <div style={{ flex: 1, overflowY: "auto" }}>{menuEl}</div>
      {isDesktop && toggleEl}
      {logoutEl(isDesktop && collapsed)}
    </div>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {!mobile && (
        <Sider width={220} theme="light" collapsible collapsed={collapsed} trigger={null} collapsedWidth={64}
          style={{ height: "100vh", position: "sticky", top: 0, background: SIDE_BG, borderRight: SIDE_BORDER }}>
          {buildSide(true)}
        </Sider>
      )}
      {mobile && (
        <Drawer placement="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
          width={240} closable={false} styles={{ body: { padding: 0, background: SIDE_BG }, header: { display: "none" } }}>
          {buildSide(false)}
        </Drawer>
      )}
      <Layout>
        {mobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#fff", color: "#1d1d1f", borderBottom: SIDE_BORDER, position: "sticky", top: 0, zIndex: 20 }}>
            <Button type="text" icon={<MenuOutlined style={{ color: "#1d1d1f", fontSize: 18 }} />} onClick={() => setDrawerOpen(true)} />
            <Logo size={22} />
            <span style={{ fontWeight: 600, fontSize: 16 }}>天能AI</span>
          </div>
        )}
        <Content style={{ margin: mobile ? 8 : 16, overflow: "auto" }}>
          <PageBoundary><Routes>
            <Route path="/personal-workspace" element={<PersonalWorkspacePage />} />
            <Route path="/legal" element={<LegalWorkbenchPage />} />
            <Route path="/personal-workspace/admin" element={<PersonalWorkspaceAdminPage />} />
            {/* 首页不挂业务模块；带采购参数的旧链接继续打开原采购工作台。 */}
            <Route path="/home" element={<HomePage items={finalItems} loaded={access.loaded} />} />
            <Route path="/" element={!hasProcurementQuery(location.search)
              ? <HomePage items={finalItems} loaded={access.loaded} />
              : onlyPublicAccess ? <Navigate to="/knowledge-contribute" replace />
              : <SupplyV2Workspace kind="procurement" access={access} />} />
            <Route path="/production" element={<ProductionPage />} />
            <Route path="/production-director" element={<SupplyGate access={access} permission="/production"><SupplyDirectorPage /></SupplyGate>} />
            <Route path="/bom-engineering" element={<SupplyLegacyRedirect to="/" view="bom" />} />
            <Route path="/purchase-forecast" element={<SupplyLegacyRedirect to="/" view="simulation" />} />
        <Route path="/price-list" element={<SupplyLegacyRedirect to="/supplier-profile" view="prices" />} />
            <Route path="/system-health" element={<SystemHealthPage />} />
            <Route path="/creative" element={<CreativePage />} />
            <Route path="/geo" element={<GeoPage />} />
            <Route path="/followup" element={<SupplyGate access={access} permission="/followup"><FollowupWorkspace /></SupplyGate>} />
            <Route path="/delivery" element={<DeliveryDashboardPage />} />
            <Route path="/dull-stock" element={<DullStockPage />} />
            <Route path="/supplier-profile" element={<SupplyV2Workspace kind="supplier" access={access} />} />
            <Route path="/ecommerce" element={<EcommercePage />} />
            <Route path="/competitor" element={<EcommercePage initial="cooperate" initialKind="competitor" />} />
            <Route path="/influencer" element={<EcommercePage initial="cooperate" initialKind="influencer" />} />
            <Route path="/investment" element={<InvestmentPage />} />
            <Route path="/investment/homedecor" element={<HomedecorPage />} />
            <Route path="/homedecor-store" element={<HomedecorStorePage />} />
            <Route path="/homedecor-finance" element={<HomedecorFinancePage />} />
            <Route path="/formula-config" element={<FormulaConfigPage />} />
            <Route path="/commission" element={<CommissionPage />} />
            <Route path="/reconciliation" element={<ReconciliationPage />} />
            {/* 2026-08-08 拆成两页。/quality 是老书签，重定向到 IQC，别让人点出 404 */}
            <Route path="/quality" element={<Navigate to="/quality/iqc" replace />} />
            <Route path="/quality/iqc" element={<QualityPage kind="IQC" />} />
            <Route path="/quality/oqc" element={<QualityPage kind="OQC" />} />
            <Route path="/product-config" element={<ProductConfigPage />} />
            <Route path="/pe-project" element={<PeProjectPage />} />
            <Route path="/pe-rnd-procurement" element={<RndProcurementPage />} />
            <Route path="/supply-rnd-procurement" element={<RndProcurementPage />} />
            <Route path="/supply-shadow" element={<SupplyLegacyRedirect to="/" view="validation" />} />
            <Route path="/pe-bom-audit" element={<PeBomAuditPage />} />
            <Route path="/eng-drawing" element={<EngDrawingPage />} />
            <Route path="/knowledge" element={<KnowledgeSuggestionPage />} />
            <Route path="/knowledge-suggestion" element={<KnowledgeSuggestionPage />} />
            <Route path="/it-knowledge" element={<ItKnowledgePage />} />
            <Route path="/kb-home" element={<KbHomePage />} />
            <Route path="/biz-knowledge" element={<BizKnowledgePage />} />
            <Route path="/eng-docs" element={<EngDocsPage />} />
            <Route path="/cross-border" element={<CrossBorderPage />} />
            <Route path="/ftcrm/workbench" element={<FtcrmWorkbenchPage />} />
            <Route path="/ftcrm/bi" element={<FtcrmBiPage />} />
            <Route path="/ftcrm/customers" element={<FtcrmCustomerPage />} />
            <Route path="/ftcrm/ask" element={<FtcrmAskPage />} />
            <Route path="/ftcrm/pipeline" element={<FtcrmPipelinePage />} />
            <Route path="/ftcrm/mail" element={<FtcrmMailPage />} />
            <Route path="/ftcrm/mail-accounts" element={<FtcrmMailAccountsPage />} />
            <Route path="/ftcrm/wecom" element={<FtcrmWecomPage />} />
            <Route path="/ftcrm/whatsapp" element={<FtcrmWhatsAppPage />} />
            <Route path="/waimao-price" element={<WaimaoPricePage />} />
            {/* 客户智能分析暂时停用；保留源码和数据，旧书签回到客户中心。 */}
            <Route path="/okki-customers/*" element={<Navigate to="/ftcrm/customers" replace />} />
            <Route path="/cockpit" element={<CockpitPage />} />
            <Route path="/executive" element={<ExecutivePage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/knowledge-contribute" element={<KnowledgeContributePage />} />
            <Route path="/customer-service" element={<CustomerServicePage />} />
            <Route path="/wuyou" element={<Navigate to="/wuyou/orders" replace />} />
            <Route path="/wuyou/agents" element={<BizServiceAgentPage />} />
            <Route path="/wuyou/files" element={<WorkorderFilesPage />} />
            <Route path="/wuyou/my-todos" element={<MyTodosPage />} />
            <Route path="/wuyou/customer360" element={<Customer360Page />} />
            <Route path="/wuyou/orders" element={<ServiceOrderPage />} />
            <Route path="/wuyou/jd-pending" element={<JdUePendingPage />} />
            <Route path="/wuyou/fee-confirm" element={<ServiceFeeConfirmPage />} />
            <Route path="/wuyou/settlements" element={<ServiceSettlementPage />} />
            <Route path="/wuyou/journeys" element={<SalesJourneyPage />} />
            <Route path="/wuyou/agent-areas" element={<ServiceAgentAreaPage />} />
            <Route path="/wuyou/sales-orders" element={<SalesOrderPage />} />
            <Route path="/wuyou/custom-orders" element={<CustomOrderPage />} />
            <Route path="/wuyou/flow" element={<WuyouFlowPage />} />
            <Route path="/wuyou/custom-config" element={<CustomConfigPage />} />
            {/* 待办/已办是同一组件按 version 切默认状态过滤(复刻 MIS 的 ?version=todo|done) */}
            <Route path="/wuyou/todo-tasks" element={<WuyouTasksPage />} />
            <Route path="/wuyou/done-tasks" element={<WuyouTasksPage done />} />
            <Route path="/wuyou/rebate" element={<RebatePage />} />
            <Route path="/wuyou/:section" element={<WuyouPage />} />
            <Route path="/system/udc" element={<UdcPage />} />
            <Route path="/ops-persist" element={<EcommercePage initial="task" />} />
            <Route path="/shipment" element={<ShipmentPage />} />
            <Route path="/account-balance" element={<AccountBalancePage />} />
            <Route path="/subsidiary-ledger" element={<SubsidiaryLedgerPage />} />
            <Route path="/cashflow" element={<CashFlowPage />} />
            <Route path="/channel-profit" element={<ChannelProfitPage />} />
            <Route path="/finance-center" element={<FinanceCenterPage />} />
            <Route path="/wanxiang" element={<EcommercePage initial="spend" />} />
            <Route path="/jd-sales" element={<EcommercePage initial="sales" />} />
            <Route path="/engineering-docs" element={<EngineeringDocPage />} />
            <Route path="/material-ack" element={<MaterialAckPage />} />
            <Route path="/admin" element={!access.loaded ? null : (access.is_admin ? <AdminPage /> : <Navigate to="/" replace />)} />
            {/* 总架构师工作台：前端这道守卫只是不让页面白晃一下，真正的闸在后端
                —— /api/ca/* 每个端点各自校验（app/services/chief_arch/authz.py），
                所以即使有人绕过前端直接敲接口，也拿不到任何东西。 */}
            <Route path="/chief-architect" element={!ca.loaded ? null : (isCA ? <ChiefArchitectPage /> : <Navigate to="/" replace />)} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes></PageBoundary>
        </Content>
      </Layout>
      {location.pathname !== "/home" && (location.pathname !== "/" || hasProcurementQuery(location.search)) && <PageBoundary quiet><ModuleChatBubble /></PageBoundary>}
        <style>{`
          /* ── 浅灰侧栏(macOS 设置风): 底色/分割线/按钮悬停; 菜单字色统一在 App.css ── */
          .ant-layout-sider {
            background: ${SIDE_BG} !important;
          }
          .ant-layout-sider .ant-menu,
          .ant-layout-sider .ant-menu-light {
            background: transparent !important;
          }
          .side-menu .ant-menu-item-divider { border-color: rgba(0,0,0,0.06); margin: 8px 14px; }
          .logout-item:hover, .side-toggle:hover {
            background: #e8e8ed !important;
            color: #1d1d1f !important;
          }
          .logout-item:hover { color: #d70015 !important; }
          @media (max-width: 768px) {
            .ant-card-body { padding: 12px !important; }
            .ant-card-head-title { font-size: 15px !important; }
            .ant-card-head { padding: 0 12px !important; }
          }
        `}</style>
    </Layout>
  );
}

const IS_WEWORK = /wxwork/i.test(navigator.userAgent);

// ── 服务商门户(师傅端 H5, /sf/*)：完全独立于员工态 ─────────────────
// 进出 /sf 都是整页加载(员工端无任何指向 /sf 的 Link)，模块级常量判断即可。
// 命中 /sf 时：不消费 wxtoken、不触发企微 OAuth、不渲染员工 LoginPage/AppLayout。
const IS_SF_PORTAL = window.location.pathname === "/sf" || window.location.pathname.startsWith("/sf/");

function SfPortalApp() {
  return (
    <ConfigProvider pagination={{ showSizeChanger: false }}>
      <BrowserRouter>
        <PageBoundary><Routes>
          <Route path="/sf" element={<Navigate to="/sf/orders" replace />} />
          <Route path="/sf/login" element={<SfLoginPage />} />
          <Route path="/sf/workspace" element={<ServiceWorkspace />} />
          <Route path="/sf/orders" element={<SfOrdersPage />} />
          <Route path="/sf/orders/:orderCode" element={<SfOrderDetailPage />} />
          <Route path="/sf/fees" element={<SfFeesPage />} />
          <Route path="*" element={<Navigate to="/sf/orders" replace />} />
        </Routes></PageBoundary>
      </BrowserRouter>
    </ConfigProvider>
  );
}

// ── 家装移动端(企微内 H5, /hd-m/*)：员工身份，但不套 PC 布局 ──────────
// 与 /sf 门户的区别：**用的是员工令牌**（同一套 wxtoken/OAuth），只是换个无壳、
// 大按钮的界面。所以它照常消费 wxtoken、照常在企微内自动静默授权，
// 仅仅是不渲染侧边菜单那套 PC chrome。
const IS_HD_MOBILE = window.location.pathname === "/hd-m"
  || window.location.pathname.startsWith("/hd-m/");

function HdMobileApp({ authed }: { authed: boolean }) {
  if (!authed) {
    // 企微内会由下面的 effect 自动跳 OAuth；这里只兜"外部浏览器直接打开"的情形
    return (
      <div style={{ minHeight: "100vh", background: "#f5f5f7", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, maxWidth: 380, textAlign: "center" }}>
          <div style={{ fontSize: 40 }}>📇</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginTop: 8 }}>家装客户 · 手机版</div>
          <div style={{ color: "#86868b", fontSize: 13.5, marginTop: 10, lineHeight: 1.9 }}>
            请从<b>企业微信</b>工作台打开本页（会自动登录）。<br />
            在电脑上请用 <a href="/">网页版</a>。
          </div>
        </div>
      </div>
    );
  }
  return (
    <BrowserRouter>
      <PageBoundary><Routes>
        <Route path="/hd-m" element={<HdmHome />} />
        <Route path="/hd-m/create" element={<HdmCreate />} />
        <Route path="/hd-m/customers" element={<HdmCustomers />} />
        <Route path="/hd-m/followup/:cid" element={<HdmFollowup />} />
        {/* 管理视角（仅业务总监；后端 /mgmt/* 也会拦一次，路由不做前端鉴权） */}
        <Route path="/hd-m/team" element={<HdmTeam />} />
        <Route path="/hd-m/team/risk" element={<HdmRisk />} />
        <Route path="*" element={<Navigate to="/hd-m" replace />} />
      </Routes></PageBoundary>
    </BrowserRouter>
  );
}

function consumeWxToken(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("wxtoken");
    if (t) {
      // 只剥离 wxtoken，保留其它业务参数（如预警卡片的 ?warn=1）
      p.delete("wxtoken");
      const rest = p.toString();
      const clean = window.location.pathname + (rest ? "?" + rest : "");
      const ok = tokenAlive(t);
      if (ok) {
        setToken(t);
        // 始终把 wxtoken 从网址剥离（含企微内）：否则令牌留在网址里被转发，别人点开就以你的身份登录(串号)。
        window.history.replaceState({}, "", clean);
      } else {
        // 过期/无效令牌：丢弃并从网址剥离，避免企微缓存旧网址导致 401→reload 死循环。
        clearToken();
        window.history.replaceState({}, "", clean);
      }
      return ok;
    }
  } catch {}
  return false;
}

export default function App() {
  // /sf 门户分支不触碰员工登录态(不消费 wxtoken、不校验员工 token)
  const [authed, setAuthed] = useState(() => { if (IS_SF_PORTAL) return false; consumeWxToken(); return tokenAlive(getToken()); });
  const handleLogout = () => { clearToken(); setAuthed(false); };

  React.useEffect(() => {
    if (IS_SF_PORTAL || authed) return;
    const p = new URLSearchParams(window.location.search);
    if (p.get("wxerr")) return;                 // 授权失败过，避免自动循环跳转
    if (/wxwork/i.test(navigator.userAgent)) {   // 在企业微信客户端内且未登录 → 自动企微授权
      // 带 next：移动页(/hd-m)授权回来要落回原页，否则被弹到 PC 首页
      const here = window.location.pathname + window.location.search;
      const nxt = here && here !== "/" ? "?next=" + encodeURIComponent(here) : "";
      window.location.href = "/api/wecom/oauth/login" + nxt;
    }
  }, [authed]);

  // 师傅端门户：独立路由树，绝不进员工登录守卫 / 管理端布局
  // (IS_SF_PORTAL 是模块级常量，渲染间不变，早退不破坏 hooks 顺序)
  if (IS_SF_PORTAL) return <SfPortalApp />;
  // 家装手机版：员工身份 + 无壳界面（同为模块级常量，早退安全）
  if (IS_HD_MOBILE) return <HdMobileApp authed={authed} />;

  return (
    <ConfigProvider
      pagination={{ showSizeChanger: false }}
      theme={{
      algorithm: theme.defaultAlgorithm,
      token: {
        colorPrimary: "#0071e3", colorInfo: "#0071e3", colorLink: "#0071e3",
        borderRadius: 12, fontFamily: APPLE_FONT, fontSize: 14,
        colorBgLayout: "#f5f5f7", colorText: "#1d1d1f", colorTextSecondary: "#6e6e73",
      },
      components: {
        Card: { borderRadiusLG: 18 },
        Button: { borderRadius: 10, controlHeight: 38, fontWeight: 500, primaryShadow: "none" },
        Segmented: { borderRadiusLG: 980, itemSelectedBg: "#ffffff" },
        Input: { borderRadius: 10 }, Select: { borderRadius: 10 }, InputNumber: { borderRadius: 10 },
        Table: { borderRadiusLG: 14, headerBg: "#f5f5f7" },
        Modal: { borderRadiusLG: 18 }, Tag: { borderRadiusSM: 6 },
      },
    }}>
      {authed ? (
        <BrowserRouter>
          <AppLayout onLogout={handleLogout} />
        </BrowserRouter>
      ) : (
        <React.Suspense fallback={<div role="status" style={{ padding: 24 }}>正在打开登录页…</div>}><LoginPage onSuccess={() => setAuthed(true)} /></React.Suspense>
      )}
    </ConfigProvider>
  );
}
